import React, { useEffect, useState } from 'react';
import axios from 'axios';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import PaginationTable from '../../pagination/Pagination4';
import ExcelJS from 'exceljs';


const API_BASE = process.env.REACT_APP_URL;
const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

function PortabilitySaleReport({ month, year, isSubmitted }) {
    const [reportData, setReportData] = useState([]);
    const [allData, setAllData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(50);
    const [searchQuery, setSearchQuery] = useState('');
    const [userRole, setUserRole] = useState('');
    const [dataTime, setDataTime] = useState("");

    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_URL}/api/ekyc-to-be-done/session-info`, {
                    withCredentials: true
                });

                const role = res.data?.userRole;

                if (role && ["1", "2", "3"].includes(String(role))) {
                    setUserRole(String(role));
                    console.log("Authorized User Role:", role);
                } else {
                    setUserRole("0");
                }
            } catch (err) {
                console.log("No valid session - blocking download");
                setUserRole("0");
            }
        };

        fetchUserRole();
    }, []);

    useEffect(() => {
        if (!isSubmitted || !month || !year) {
            setReportData([]);
            setAllData([]);
            return;
        }

        const fetchReport = async () => {
            setLoading(true);
            setError(null);
            try {
                const res = await axios.get(`${API_BASE}/api/report/epos`, {
                    params: { month, year },
                    timeout: 90000
                });

                const data = res.data || [];
                if (data.length === 0) {
                    setError("No data found for selected month/year.");
                }
                setReportData(data);
                setAllData(data);

                // Extract dataTime from response
                const rawDataTime = res.data.dataTime || res.data.data_time || res.data.datatime || "";
                let formattedTime = "";
                if (rawDataTime) {
                    try {
                        const date = new Date(rawDataTime);
                        if (!isNaN(date.getTime())) {
                            const day = String(date.getDate()).padStart(2, '0');
                            const monthVal = String(date.getMonth() + 1).padStart(2, '0');
                            const yearVal = date.getFullYear();
                            let hours = date.getHours();
                            const minutes = String(date.getMinutes()).padStart(2, '0');
                            const seconds = String(date.getSeconds()).padStart(2, '0');
                            const ampm = hours >= 12 ? 'PM' : 'AM';
                            hours = hours % 12 || 12;
                            const strHours = String(hours).padStart(2, '0');
                            formattedTime = `${day}-${monthVal}-${yearVal} ${strHours}:${minutes}:${seconds} ${ampm}`;
                        } else {
                            formattedTime = rawDataTime.replace('T', ' ').substring(0, 19);
                        }
                    } catch (e) {
                        formattedTime = rawDataTime.replace('T', ' ').substring(0, 19);
                    }
                } else if (data.length > 0) {
                    // Fallback to searching in rows
                    const dTimeItem = data.find(item => item.dataTime || item.data_time || item.datatime || item.DATA_TIME);
                    const rowDTime = dTimeItem ? (dTimeItem.dataTime || dTimeItem.data_time || dTimeItem.datatime || dTimeItem.DATA_TIME) : "";
                    if (rowDTime) {
                        try {
                            const date = new Date(rowDTime);
                            if (!isNaN(date.getTime())) {
                                const day = String(date.getDate()).padStart(2, '0');
                                const monthVal = String(date.getMonth() + 1).padStart(2, '0');
                                const yearVal = date.getFullYear();
                                let hours = date.getHours();
                                const minutes = String(date.getMinutes()).padStart(2, '0');
                                const seconds = String(date.getSeconds()).padStart(2, '0');
                                const ampm = hours >= 12 ? 'PM' : 'AM';
                                hours = hours % 12 || 12;
                                const strHours = String(hours).padStart(2, '0');
                                formattedTime = `${day}-${monthVal}-${yearVal} ${strHours}:${minutes}:${seconds} ${ampm}`;
                            } else {
                                formattedTime = rowDTime.replace('T', ' ').substring(0, 19);
                            }
                        } catch (e) {
                            formattedTime = rowDTime.replace('T', ' ').substring(0, 19);
                        }
                    }
                }
                setDataTime(formattedTime);
            } catch (err) {
                console.error("Public report API error:", err);
                setError(" Please try again later.");
            } finally {
                setLoading(false);
            }
        };

        fetchReport();
    }, [month, year, isSubmitted]);

    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const filteredData = reportData.filter(item =>
        (item.district?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (item.mandal?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
        (item.fpsId?.toString() || '').includes(searchQuery.toLowerCase())
    );

    const paginatedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    const startSerialNo = page * rowsPerPage + 1;

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (value) => {
        setRowsPerPage(value === 'all' ? filteredData.length : parseInt(value, 10));
        setPage(0);
    };

    const downloadExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Portability Report');

        const headingText = `Total Sale Cards Details - ${monthNames[month - 1]} / ${year}`;

        // 👉 Heading row
        const headingRow = worksheet.addRow([headingText]);
        worksheet.mergeCells('A1:K1');

        headingRow.font = { bold: true, size: 14 };
        headingRow.alignment = { horizontal: 'center', vertical: 'middle' };
        worksheet.getRow(1).height = 30;
        // worksheet.addRow([]);

        const headerRow = worksheet.addRow([
            "S.No",
            "District",
            "Mandal",
            "FPS ID",
            "Total Cards(A)",
            "Self Drawn(B)",
            "Portability Cards(C)",
            "Total Drawn(D)",
            "Self Sale %(B/A)",
            "Portability %(C/A)",
            "Total Drawl RC %(D/A)"
        ]);

        headerRow.eachCell(cell => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF000000' }
            };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
        });

        allData.forEach((row, index) => {
            worksheet.addRow([
                index + 1,
                row.district || '-',
                row.mandal || '-',
                row.fpsId,
                row.totalCards || 0,
                row.selfDrawnCards || 0,
                row.portCards || 0,
                row.totalDrawnCards || 0,
                `${(row.selfPercentage || 0).toFixed(2)}%`,
                `${(row.portPercentage || 0).toFixed(2)}%`,
                `${(row.totalRcPercentage || 0).toFixed(2)}%`
            ]);
        });

        worksheet.columns = [
            { width: 8 },
            { width: 20 },
            { width: 20 },
            { width: 15 },
            { width: 18 },
            { width: 18 },
            { width: 22 },
            { width: 18 },
            { width: 18 },
            { width: 20 },
            { width: 22 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `Portability_Sale_Report_${month}_${year}.xlsx`;
        link.click();
    };




    if (!isSubmitted) return null;

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '500px' }}>
                <Box sx={{ display: 'flex' }}>
                    <CircularProgress size={50} thickness={2} />
                </Box>
            </div>
        );
    }

    if (error) {
        return <div align="center" style={{ color: "red", fontSize: "22px", margin: "60px" }}>{error}</div>;
    }

    if (reportData.length === 0) {
        return <div align="center" style={{ fontSize: "22px", margin: "60px" }}>No data found</div>;
    }

    const allowedRoles = ["1", "2", "3"];
    const canDownload = allowedRoles.includes(userRole);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 10% 20px 0' }}>
                <input
                    type="text"
                    placeholder="Search FPS ID, District, Mandal..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{
                        padding: '12px 20px',
                        width: '200px',
                        fontSize: '16px',
                        border: '2px solid #000',
                        borderRadius: '8px'
                    }}
                />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginRight: '10%', marginBottom: '10px' }}>
                <PaginationTable
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>

            <table align="center" width="90%" cellPadding="12" cellSpacing="0"
            >
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#000', color: '#fff', zIndex: 1 }}>
                    <tr className="tableheader">
                        <th colSpan={11} >
                            Ration Card Drawl Percentage - {monthNames[month - 1]} / {year}
                        </th>
                    </tr>
                    <tr className="tableheader">
                        <th>S.No</th>
                        <th>District</th>
                        <th>Mandal</th>
                        <th>FPS ID</th>
                        <th>Total Cards(A)</th>
                        <th>Self Drawn(B)</th>
                        <th>Portability Cards(C)</th>
                        <th>Total Drawn(D)</th>
                        <th>Self Sale %(B/A)</th>
                        <th>Portability %(C/A)</th>
                        <th>Total Drawl RC %(D/A)</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedData.map((row, i) => (
                        <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#f0f0f0" : "white" }}>
                            <td align="center">{startSerialNo + i}</td>
                            <td align="center">{row.district || '-'}</td>
                            <td align="center">{row.mandal || '-'}</td>
                            <td align="center">{row.fpsId}</td>
                            <td align="right">{row.totalCards?.toLocaleString() || 0}</td>
                            <td align="right">{row.selfDrawnCards?.toLocaleString() || 0}</td>
                            <td align="right">{row.portCards?.toLocaleString() || 0}</td>
                            <td align="right">{row.totalDrawnCards?.toLocaleString() || 0}</td>
                            <td align="right" >
                                {(row.selfPercentage || 0).toFixed(2)}%
                            </td>
                            <td align="right" >
                                {(row.portPercentage || 0).toFixed(2)}%
                            </td>
                            <td align="right" >
                                {(row.totalRcPercentage || 0).toFixed(2)}%
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {dataTime && (
                <div style={{ width: "90%", textAlign: 'right', margin: '10px auto 0', fontWeight: 'bold' }}>
                    Last Refresh Time: {dataTime}
                </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '20px 10%', flexWrap: 'wrap', gap: '20px' }}>
                <div style={{ flex: 1, minWidth: '300px' }}>
                    <PaginationTable
                        count={filteredData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </div>

                {canDownload && (
                    <button
                        onClick={downloadExcel}
                        className='button button2'
                    >
                        Download Excel
                    </button>
                )}
            </div>

            <div style={{ height: '100px' }}></div>
        </>
    );
}

export default PortabilitySaleReport;